import { useEffect, useRef } from 'react';

const MENU_HOVER_SELECTOR = '[data-menu-item]';
const MASCOT_SELECTOR = '[data-mascot]';
const MAX_PEEK_OFFSET = 1.8;
const TRACK_RANGE_PX = 280;

interface Props {
  mode?: 'guest' | 'authed';
  initial?: string;
  onClick?: () => void;
  className?: string;
  ariaLabel?: string;
  /** Tailwind height/width utilities for the button box. Defaults to the desktop size. */
  sizeClass?: string;
}

type MascotState = 'idle' | 'peek' | 'shy';

const BODY_PATH =
  'M 25.794 28.168 C 27.815 30.408 29.049 33.326 29.049 36.531 ' +
  'C 29.049 36.977 29.024 37.417 28.978 37.851 L 26.308 37.851 ' +
  'C 26.367 37.418 26.408 36.98 26.408 36.531 ' +
  'C 26.408 33.875 25.328 31.47 23.581 29.666 ' +
  'C 21.292 30.95 18.656 31.69 15.845 31.69 ' +
  'C 13.036 31.69 10.403 30.951 8.115 29.67 ' +
  'C 6.366 31.473 5.282 33.874 5.282 36.531 ' +
  'C 5.282 36.98 5.323 37.418 5.382 37.851 L 2.711 37.851 ' +
  'C 2.665 37.413 2.641 36.972 2.641 36.531 ' +
  'C 2.641 33.326 3.872 30.406 5.891 28.165 ' +
  'C 2.3 25.26 0 20.823 0 15.845 C 0 7.094 7.094 0 15.845 0 ' +
  'C 24.596 0 31.69 7.094 31.69 15.845 ' +
  'C 31.69 20.825 29.388 25.263 25.794 28.168 Z ' +
  'M 15.845 10.902 C 8.552 10.902 2.641 7.922 2.641 15.439 ' +
  'C 2.641 22.955 8.552 29.049 15.845 29.049 ' +
  'C 23.137 29.049 29.049 22.955 29.049 15.439 ' +
  'C 29.049 7.922 23.137 10.902 15.845 10.902 Z';

const MOUTH_IDLE_PATH =
  'M 21.126 23.327 C 21.126 24.542 18.762 25.528 15.845 25.528 ' +
  'C 12.928 25.528 10.563 24.542 10.563 23.327 ' +
  'C 10.563 23.014 10.726 22.717 11.009 22.447 ' +
  'C 11.825 23.223 13.68 23.767 15.845 23.767 ' +
  'C 18.009 23.767 19.865 23.223 20.681 22.447 ' +
  'C 20.964 22.717 21.126 23.014 21.126 23.327 Z';

function twinkleAt(cx: number, cy: number, size = 1): string {
  const s = size;
  return (
    `M ${cx} ${cy - 1.8 * s} ` +
    `L ${cx + 0.45 * s} ${cy - 0.45 * s} ` +
    `L ${cx + 1.8 * s} ${cy} ` +
    `L ${cx + 0.45 * s} ${cy + 0.45 * s} ` +
    `L ${cx} ${cy + 1.8 * s} ` +
    `L ${cx - 0.45 * s} ${cy + 0.45 * s} ` +
    `L ${cx - 1.8 * s} ${cy} ` +
    `L ${cx - 0.45 * s} ${cy - 0.45 * s} Z`
  );
}

function heartAt(cx: number, cy: number, size = 1): string {
  const s = size;
  return (
    `M ${cx} ${cy + 1.5 * s} ` +
    `C ${cx - 1.5 * s} ${cy + 0.3 * s} ${cx - 3 * s} ${cy - 1 * s} ${cx - 3 * s} ${cy - 2.5 * s} ` +
    `C ${cx - 3 * s} ${cy - 3.5 * s} ${cx - 1.5 * s} ${cy - 4 * s} ${cx} ${cy - 2.5 * s} ` +
    `C ${cx + 1.5 * s} ${cy - 4 * s} ${cx + 3 * s} ${cy - 3.5 * s} ${cx + 3 * s} ${cy - 2.5 * s} ` +
    `C ${cx + 3 * s} ${cy - 1 * s} ${cx + 1.5 * s} ${cy + 0.3 * s} ${cx} ${cy + 1.5 * s} Z`
  );
}

export default function UserMascot({
  mode,
  initial,
  onClick,
  className = '',
  ariaLabel = 'Mon compte',
  sizeClass = 'h-12 w-12',
}: Props) {
  const rootRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    // Idle/peek/shy state tracking is mouse-only — skip on touch devices
    // where matchMedia(pointer: fine) is false. Saves the pointermove
    // handler and the CSS keyframe layers it would activate.
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      try {
        if (!window.matchMedia('(pointer: fine)').matches) return;
      } catch { /* fall through */ }
    }

    let rafId = 0;
    let pendingX = 0;
    let pendingY = 0;
    let pendingTarget: Element | null = null;
    let scheduled = false;

    const setPeek = (px: number, py: number) => {
      root.style.setProperty('--peek-x', px.toFixed(2));
      root.style.setProperty('--peek-y', py.toFixed(2));
    };

    const setState = (next: MascotState) => {
      if (root.dataset.state !== next) root.dataset.state = next;
    };

    const flush = () => {
      scheduled = false;
      const target = pendingTarget;
      const overSelf = !!target?.closest?.(MASCOT_SELECTOR);
      const overMenu = !!target?.closest?.(MENU_HOVER_SELECTOR);
      const next: MascotState = overSelf ? 'shy' : overMenu ? 'peek' : 'idle';
      setState(next);

      if (next === 'peek') {
        const rect = root.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const nx = Math.max(-1, Math.min(1, (pendingX - cx) / TRACK_RANGE_PX));
        const ny = Math.max(-1, Math.min(1, (pendingY - cy) / TRACK_RANGE_PX));
        setPeek(nx * MAX_PEEK_OFFSET, ny * MAX_PEEK_OFFSET);
      } else {
        setPeek(0, 0);
      }
    };

    const onMove = (e: PointerEvent) => {
      pendingX = e.clientX;
      pendingY = e.clientY;
      pendingTarget = e.target as Element | null;
      if (!scheduled) {
        scheduled = true;
        rafId = requestAnimationFrame(flush);
      }
    };

    const onWindowBlur = () => {
      setState('idle');
      setPeek(0, 0);
    };

    document.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('blur', onWindowBlur);

    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('pointermove', onMove);
      window.removeEventListener('blur', onWindowBlur);
    };
  }, []);

  return (
    <button
      ref={rootRef}
      type="button"
      data-mascot
      data-state="idle"
      data-fx-mute
      onClick={onClick}
      aria-label={ariaLabel}
      title={ariaLabel}
      className={`user-mascot relative inline-flex items-center justify-center ${sizeClass} cursor-pointer p-0 bg-transparent border-0 text-ink ${className}`}
    >
      <svg viewBox="-3 -4 42 48" className="mascot-svg" aria-hidden="true">
        <g className="mascot-body">
          <g className="mascot-body-inner">
            <path
              className="mascot-shape"
              d={BODY_PATH}
              fillRule="nonzero"
              fill="currentColor"
            />

            <ellipse className="mascot-blush mascot-blush-l" cx="6.5" cy="22" rx="2.4" ry="1.4" />
            <ellipse className="mascot-blush mascot-blush-r" cx="25.2" cy="22" rx="2.4" ry="1.4" />

            <g className="mascot-eyes-open">
              <circle className="mascot-eye mascot-eye-l" cx="9.683" cy="16.725" r="1.95" />
              <circle className="mascot-eye mascot-eye-r" cx="22.007" cy="16.725" r="1.95" />
              <circle className="mascot-spark mascot-spark-l" cx="10.4" cy="16.05" r="0.55" />
              <circle className="mascot-spark mascot-spark-r" cx="22.7" cy="16.05" r="0.55" />
            </g>

            <g className="mascot-eyes-shy">
              <path d="M 7.6 17.1 Q 9.683 14.3 11.8 17.1" />
              <path d="M 19.9 17.1 Q 22.007 14.3 24.1 17.1" />
            </g>

            <g className="mascot-eyes-heart" aria-hidden="true">
              <path className="mascot-eye-h mascot-eye-h-l" d={heartAt(9.683, 16.725, 1.0)} />
              <path className="mascot-eye-h mascot-eye-h-r" d={heartAt(22.007, 16.725, 1.0)} />
              <circle className="mascot-eye-h-shine mascot-eye-h-shine-l" cx="8.5" cy="15.4" r="0.45" />
              <circle className="mascot-eye-h-shine mascot-eye-h-shine-r" cx="20.8" cy="15.4" r="0.45" />
            </g>

            <g className="mascot-twinkles" aria-hidden="true">
              <path className="mascot-twinkle mascot-twinkle-1" d={twinkleAt(6.2, 13.4, 0.95)} />
              <path className="mascot-twinkle mascot-twinkle-2" d={twinkleAt(25.4, 13.4, 0.95)} />
            </g>

            <path className="mascot-mouth mascot-mouth-idle" d={MOUTH_IDLE_PATH} />
            <ellipse className="mascot-mouth mascot-mouth-peek" cx="15.845" cy="24.8" rx="3.7" ry="3.1" />
            <g className="mascot-tongue" aria-hidden="true">
              <path
                className="mascot-tongue-shape"
                d="M 12.7 25.4 Q 12.5 29.5 15.845 30.2 Q 19.2 29.5 19.0 25.4 Z"
              />
              <path
                className="mascot-tongue-mid"
                d="M 15.845 26 L 15.845 29.6"
              />
            </g>
            <path className="mascot-mouth mascot-mouth-shy" d="M 11 23 Q 15.845 26.4 20.7 23" />

            <g className="mascot-drool">
              <ellipse className="mascot-drop mascot-drop-1" cx="12.5" cy="27.6" rx="1.25" ry="2.0" />
              <ellipse className="mascot-drop mascot-drop-2" cx="19.2" cy="27.6" rx="1.25" ry="2.0" />
              <ellipse className="mascot-drop mascot-drop-3" cx="12.3" cy="29.6" rx="0.85" ry="1.4" />
              <ellipse className="mascot-drop mascot-drop-4" cx="19.4" cy="29.6" rx="0.85" ry="1.4" />
              <ellipse className="mascot-drop mascot-drop-5" cx="15.845" cy="30.6" rx="1.05" ry="1.7" />
            </g>

            <g className="mascot-heart-burst" aria-hidden="true">
              <path className="mascot-mh mascot-mh-1" d={heartAt(11, 35, 0.95)} />
              <path className="mascot-mh mascot-mh-2" d={heartAt(21, 35, 1.05)} />
              <path className="mascot-mh mascot-mh-3" d={heartAt(7, 36, 0.8)} />
              <path className="mascot-mh mascot-mh-4" d={heartAt(25, 36, 0.85)} />
              <path className="mascot-mh mascot-mh-5" d={heartAt(16, 34, 1.15)} />
              <path className="mascot-mh mascot-mh-6" d={heartAt(14, 36, 0.9)} />
              <path className="mascot-mh mascot-mh-7" d={heartAt(18, 35, 1.0)} />
            </g>
          </g>
        </g>

        <g className="mascot-arm">
          <path className="mascot-arm-line" d="M 27 13 Q 32 7 35 2" />
          <circle className="mascot-hand" cx="35" cy="2" r="2.4" />
        </g>
      </svg>
      {mode === 'authed' && initial && (
        <span
          aria-hidden="true"
          className="absolute top-0 right-0 w-4 h-4 inline-flex items-center justify-center bg-ink text-paper font-display text-[10px] leading-none rounded-full border-2 border-paper"
        >
          {initial}
        </span>
      )}
    </button>
  );
}
